// src/components/GridView/hooks/useGridColumns.ts
import { useMemo } from 'react';
import { GRID_TITLE_COLUMN_WIDTH, FIELD_TYPES } from '../types';
import type { GridColumn } from '../types';
import type { ConversationDetail } from '@/types/conversation';

// ==================== Width estimation ====================

const CHAR_PX = 7.5; // approximate pixels per character (13px Roboto)
const HEADER_PAD = 40; // header padding + sort icon space
const CELL_PAD = 18; // cell left+right padding
const COL_MIN = 80;
const COL_MAX = 320;
const DATE_WIDTH = 40; // "MM/DD/YYYY" fixed

/** Estimates column width from header title and the set of cell values seen in data. */
function estimateWidth(title: string, values: string[]): number
{
  const headerPx = title.length * CHAR_PX + HEADER_PAD;
  const dataPx = values.reduce((max, v) =>
  {
    const len = String(v ?? '').length * CHAR_PX + CELL_PAD;
    return len > max ? len : max;
  }, 0);
  return Math.round(Math.min(Math.max(headerPx, dataPx, COL_MIN), COL_MAX));
}

// ==================== Hook ====================

interface UseGridColumnsReturn {
  columns: GridColumn[];
  columnMap: Map<string, GridColumn>;
}

/**
 * Derives the set of grid columns from the loaded conversations.
 *
 * Fixed columns (always present):
 *  - col-title        (card title — fixed wide)
 *  - col-created-at   (creation date — fixed narrow)
 *
 * Dynamic columns: one per unique (LayerId, CustomFieldId) pair found across
 * all conversations' LayerCustomFields.  Width is estimated from header title
 * length and the maximum value length seen in the loaded data.
 */
export function useGridColumns(conversations: ConversationDetail[]): UseGridColumnsReturn
{
  return useMemo(() =>
  {
    const fixedColumns: GridColumn[] = [
      {
        key: 'col-title',
        title: 'Project/Task name',
        width: GRID_TITLE_COLUMN_WIDTH,
      },
      {
        key: 'col-created-at',
        title: 'Created At',
        width: DATE_WIDTH,
      },
    ];

    // Pass 1 — collect unique columns keyed by (LayerId, fieldName) and accumulate
    // all customFieldIds + values.  Fields that share the same layer and name but have
    // different CustomFieldIds (a backend duplication pattern) are merged into one column.
    const dynamicMap = new Map<
      string,
      GridColumn & {
        layerId: number;
        layerName: string;
        layerColor: string;
        sortKey: string;
        values: string[];
        customFieldIds: number[];
      }
    >();

    for (const conv of conversations)
    {
      if (!conv.LayerCustomFields) continue;
      for (const layer of conv.LayerCustomFields)
      {
        if (!layer.CustomFields) continue;
        for (const cf of layer.CustomFields)
        {
          const fieldName = (cf.FieldName ?? '').trim();
          const key = `layer-${layer.LayerId}-${fieldName.toLowerCase()}`;
          if (!dynamicMap.has(key))
          {
            dynamicMap.set(key, {
              key,
              title: fieldName,
              layerId: layer.LayerId,
              layerName: layer.LayerName,
              layerColor: layer.Color,
              customFieldId: cf.CustomFieldId,
              customFieldIds: [cf.CustomFieldId],
              fieldTypeId: cf.FieldTypeId,
              width: COL_MIN,
              serviceUrl: cf.ServiceUrl,
              textField: cf.TextField,
              valueField: cf.ValueField,
              sortKey: `${String(layer.LayerId).padStart(10, '0')}-${String(cf.OrderInLayer ?? 9999).padStart(10, '0')}`,
              values: [],
            });
          }
          const entry = dynamicMap.get(key)!;
          // Accumulate all IDs (deduped) for this unified column
          if (!entry.customFieldIds.includes(cf.CustomFieldId))
          {
            entry.customFieldIds.push(cf.CustomFieldId);
          }
          const val = String(cf.FieldValue ?? '');
          if (val) entry.values.push(val);
        }
      }
    }

    // Pass 2 — compute width from title + collected values, then strip internals
    const dynamicColumns = Array.from(dynamicMap.values())
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
      .map(({ sortKey: _sortKey, values, ...col }) => ({
        ...col,
        width: estimateWidth(col.title, values),
      }));

    // The last dynamic column is always the rightmost visible column.  When a vertical
    // scrollbar is present it overlaps ~8–17 px of that column, clipping the cell text.
    // Adding a buffer ensures the content stays fully visible regardless of scrollbar width.
    const LAST_COL_SCROLLBAR_BUFFER = 50;
    if (dynamicColumns.length > 0)
    {
      const last = dynamicColumns[dynamicColumns.length - 1]!;
      last.width = Math.min((last.width ?? COL_MIN) + LAST_COL_SCROLLBAR_BUFFER, COL_MAX);
    }

    const columns: GridColumn[] = [...fixedColumns, ...dynamicColumns];

    const columnMap = new Map<string, GridColumn>();
    for (const col of columns)
    {
      columnMap.set(col.key, col);
    }

    return { columns, columnMap };
  }, [conversations]);
}

export interface IsCellEnabledOptions {
  /** Skip the row-level phase block. Used by views that mix phase and regular card rows. */
  allowPhaseRows?: boolean;
}

/**
 * Returns true if the conversation has the custom field referenced by the column.
 * Blocks phase rows entirely unless `allowPhaseRows` is set.
 */
export function isCellEnabled(
  conversation: ConversationDetail,
  column: GridColumn,
  options?: IsCellEnabledOptions,
): boolean
{
  if (!options?.allowPhaseRows)
  {
    // If this conversation IS a phase, the entire row is read-only
    if (conversation.ConversationPhaseId != null && conversation.ConversationPhaseId > 0)
    {
      return false;
    }
    if ((conversation as any).CoreTaskPhaseId != null && (conversation as any).CoreTaskPhaseId > 0)
    {
      return false;
    }

    // If this conversation HAS phases, the entire row is read-only
    if (conversation.HasPhases)
    {
      return false;
    }
  }

  if (!column.layerId) return false;
  if (!conversation.LayerCustomFields) return false;

  const layer = conversation.LayerCustomFields.find(l => l.LayerId === column.layerId);
  if (!layer) return false;

  const ids = column.customFieldIds ?? (column.customFieldId != null ? [column.customFieldId] : []);
  return layer.CustomFields.some(cf => ids.includes(cf.CustomFieldId));
}

/** Lowercased so header casing differences can't unblock a cell. */
const PHASE_AGGREGATE_FIELD_NAMES = new Set(['estimated', 'remaining', 'actual']);

/**
 * Read-only rule from the legacy Phase Details grids:
 * rows with phases block Estimated/Remaining/Actual and people fields; other rows block
 * only Actual, and only for non-admins.
 */
export function isPhaseAggregateCell(
  conversation: ConversationDetail,
  column: GridColumn,
  isAdmin: boolean,
): boolean
{
  const fieldName = (column.title ?? '').trim().toLowerCase();

  if (conversation.HasPhases)
  {
    return (
      PHASE_AGGREGATE_FIELD_NAMES.has(fieldName) ||
      column.fieldTypeId === FIELD_TYPES.MULTI_SELECT
    );
  }

  return !isAdmin && fieldName === 'actual';
}

/**
 * Returns true when the cell is a summatory field, flagged by the API via
 * SummatoryValuePhases / SummatoryPeoplePhases. Required by views passing `allowPhaseRows`.
 */
export function isCellSummatory(conversation: ConversationDetail, column: GridColumn): boolean
{
  if (!column.layerId || !conversation.LayerCustomFields) return false;

  const layer = conversation.LayerCustomFields.find(l => l.LayerId === column.layerId);
  if (!layer) return false;

  const ids = column.customFieldIds ?? (column.customFieldId != null ? [column.customFieldId] : []);
  return layer.CustomFields.some(cf =>
  {
    if (!ids.includes(cf.CustomFieldId)) return false;

    // Check API-provided summatory flags
    return (cf as any).SummatoryValuePhases === true || (cf as any).SummatoryPeoplePhases === true;
  });
}
